import { DataTable, type Column } from "@/components/ui/DataTable";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { ErrorState } from "@/components/ui/States";
import { Icon } from "@/components/ui/Icon";
import { useItem } from "@/lib/api/queries";
import { dateTime, relative } from "@/lib/format";

/**
 * «1C holati» — avtomatik mapping natijasi, FAQAT O'QISH.
 *
 * Qo'lda mapping 2026-10-31 da olib tashlandi (buyurtmachi qarori):
 * o'lchov birligi, ombor va «Склад получатель» 1C OData dan o'zi
 * aniqlanadi. Bu yerda faqat aniqlanmaganlari ko'rinadi — ularni
 * panelda «to'g'irlab» bo'lmaydi: qiymat 1C da tuzatiladi (masalan
 * `коробка=600шт` birligi) yoki 1C administratori OData foydalanuvchisiga
 * huquq beradi, va xabar / zakas o'zi yangilanadi.
 *
 * GUID lar ko'rsatilmaydi — xodimga hech narsa demaydi.
 */
interface WaitingValue {
  kind: string;
  external_key: string;
  messages: number;
}

interface RecipientItem {
  name: string;
  resolved: boolean;
  display: string;
  order_count: number;
  last_seen?: string;
}

interface OneCStatus {
  waiting_values: WaitingValue[];
  unit_classifier: { units: number; loaded_at?: string; last_error?: string };
  recipients?: {
    enabled: boolean;
    unresolved_keys: number;
    unresolved_orders: number;
    queue: { PENDING?: number; DONE?: number; NOT_FOUND?: number };
    items: RecipientItem[];
  };
}

const KIND_LABEL: Record<string, string> = {
  UOM: "O'lchov birligi",
  WAREHOUSE: "Ombor",
  ROLE: "Lavozim",
};

export function OneCStatusPanel() {
  const status = useItem<OneCStatus>("integration", "/api/v1/integration/onec-status");
  const data = status.data;
  const recipients = data?.recipients;
  const resolved = (recipients?.items ?? []).filter((item) => item.resolved);

  const waitingColumns: Array<Column<WaitingValue>> = [
    {
      key: "kind",
      header: "Turi",
      value: (row) => row.kind,
      render: (row) => <StatusBadge value={KIND_LABEL[row.kind] ?? row.kind} tone="warning" />,
    },
    { key: "value", header: "1C qiymati", value: (row) => row.external_key, render: (row) => <span className="mono">{row.external_key}</span> },
    { key: "messages", header: "Kutayotgan xabarlar", align: "right", value: (row) => row.messages, render: (row) => row.messages },
  ];

  const recipientColumns: Array<Column<RecipientItem>> = [
    { key: "name", header: "Qabul qiluvchi ombor (1C)", value: (row) => row.name, render: (row) => row.name },
    { key: "orders", header: "Zakaslar", align: "right", value: (row) => row.order_count, render: (row) => row.order_count },
    {
      key: "seen",
      header: "Oxirgi",
      value: (row) => row.last_seen,
      render: (row) => (row.last_seen ? <span title={dateTime(row.last_seen)}>{relative(row.last_seen)}</span> : "—"),
    },
  ];

  if (status.error) return <ErrorState error={status.error} onRetry={() => void status.refetch()} />;

  return (
    <>
      <section className="section">
        <div className="section-title">Avtomatik bog'lanmagan 1C qiymatlari</div>
        <div className="notice" style={{ marginBottom: 12 }}>
          <Icon name="integration" size={16} />
          <span>
            Mapping avtomatik: birlik — lug'at va 1C tasniflagichi bo'yicha
            {data?.unit_classifier.loaded_at
              ? ` (${data.unit_classifier.units} ta birlik, ${relative(data.unit_classifier.loaded_at)} o'qilgan)`
              : " (tasniflagich hali o'qilmagan)"}
            , ombor — A / DEFAULT. Qadoqli birlik (masalan «коробка=600шт») ataylab bog'lanmaydi —
            1C da tuzating, xabar o'zi davom etadi.
          </span>
        </div>
        <DataTable
          columns={waitingColumns}
          rows={data?.waiting_values}
          rowKey={(row) => `${row.kind}:${row.external_key}`}
          isLoading={status.isLoading}
          emptyTitle="Hammasi bog'langan"
          emptyHint="1C dan kelgan barcha birlik va omborlar avtomatik aniqlandi."
        />
      </section>

      {recipients && (
        <section className="section">
          <div className="section-title">«Склад получатель» (qabul qiluvchi ombor)</div>
          {!recipients.enabled && (
            <div className="notice" style={{ marginBottom: 12 }}>
              <Icon name="alert" size={16} />
              <span>
                OData manzili sozlanmagan — rekvizit 1C dan o'qilmaydi.
              </span>
            </div>
          )}
          {recipients.unresolved_keys > 0 && (
            <div className="notice" style={{ marginBottom: 12 }}>
              <Icon name="alert" size={16} />
              <span>
                {recipients.unresolved_keys} ta qabul ombori 1C da aniqlanmadi ({recipients.unresolved_orders} ta
                zakasda «Qabul ombori aniqlanmagan»). 1C administratori OData foydalanuvchisiga GOLD MIRACLE
                omborlarini o'qish huquqini bergach, nomlar o'zi paydo bo'ladi (har 6 soatda tekshiriladi).
              </span>
            </div>
          )}
          <DataTable
            columns={recipientColumns}
            rows={resolved}
            rowKey={(row) => row.name}
            isLoading={status.isLoading}
            emptyTitle="Aniqlangan qabul ombori yo'q"
            emptyHint="Nomlar 1C dan o'qilgach shu yerda chiqadi."
            footer={
              <span>
                Navbatda {recipients.queue.PENDING ?? 0} · o'qilgan {recipients.queue.DONE ?? 0} · 1C da topilmagan{" "}
                {recipients.queue.NOT_FOUND ?? 0}
              </span>
            }
          />
        </section>
      )}
    </>
  );
}
